
import { useEffect, useState } from 'react'
import Link from 'next/link'
import { Wordmark } from '@/components/workspace/Logo'
import { btn, cx } from '@/lib/ui'

/**
 * The bar that follows a visitor down the page: the wordmark and the two
 * ways in, edge to edge. It stays out of the way while the wordmark at the top of the hero is
 * on screen, and slides in once that has scrolled past — so the page opens
 * with one logo, not two.
 */
export function StickyBar({ signupsOpen, watch }: { signupsOpen: boolean; watch: string }) {
  const [shown, setShown] = useState(false)

  useEffect(() => {
    const target = document.getElementById(watch)
    if (!target) return
    const observer = new IntersectionObserver(
      ([entry]) => setShown(!entry.isIntersecting && entry.boundingClientRect.top < 0),
      { threshold: 0 },
    )
    observer.observe(target)
    return () => observer.disconnect()
  }, [watch])

  return (
    <div
      aria-hidden={!shown}
      className={cx(
        'glass fixed inset-x-0 top-0 z-40 rounded-none border-x-0 border-t-0 motion-safe:transition-[transform,opacity] motion-safe:duration-200',
        shown ? 'translate-y-0 opacity-100' : 'pointer-events-none -translate-y-full opacity-0',
      )}
    >
      <div className="mx-auto flex h-14 w-full max-w-6xl items-center justify-between px-5 sm:px-8">
        <Link href="/" aria-label="ROUTR" className="flex items-center" tabIndex={shown ? 0 : -1}>
          <Wordmark className="h-5 w-auto text-[var(--ink)] sm:h-6" />
        </Link>
        <nav aria-label="Account" className="flex items-center gap-2">
          <Link href="/login" className={cx(btn.sm, btn.secondary)} tabIndex={shown ? 0 : -1}>
            Sign in
          </Link>
          {signupsOpen && (
            <Link href="/register" className={cx(btn.sm, btn.primary)} tabIndex={shown ? 0 : -1}>
              Create an account
            </Link>
          )}
        </nav>
      </div>
    </div>
  )
}
